import { useEffect, useState, useCallback } from 'react';
import { listUsersManage, createUser, updateUser, resetUserPassword, moveUser, getCompanies, getMe } from '../api';
import { isSuperAdmin } from '../utils/audience';
import { ASSIGNABLE_ROLES, ROLE_DESCRIPTIONS, ROLE_TOOLTIP, roleLabel, roleOptionText, userStatus } from '../utils/userRoles';

/* W-053 step 3a (2026-10-07) -- THE ADD USER TAB (Brian's design, 10-07). Company admins and the super admin; never a
   viewer (the tab is not offered -- AdminPage). The server is the authority for every rule (last admin, self-deactivate,
   reset-self, the super admin account); this panel shows its { error } as it comes.

   THE TEMPORARY PASSWORD (after Add or Reset) is shown ONCE: it lives only in this component's state, is cleared when its box
   is dismissed and when the tab is left (the panel unmounts) or the company changes. It is never written to localStorage /
   sessionStorage, never logged, never put in a URL.

   CONFIRMATIONS: Deactivate, Reset password and demoting YOURSELF ask first, naming the user. Other role changes and Reactivate
   act at once. On your own row Deactivate and Reset are disabled (the server refuses them; use Change password).
   MOVE (super admin only): pick the company and the role, confirm naming from -> to; the row then leaves this list. */

const FONT = "'DM Sans',-apple-system,sans-serif";
const th = { padding: '10px 12px', fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', textAlign: 'left' };
const td = { padding: '10px 12px', color: '#1A1C2E', verticalAlign: 'top' };
/* W-053 3b.2: the buttons use ONE small set, index.css .fl-btn (+ --blue / --red / --green / --grey / --primary): Change role,
   Reset password, Move to another company -> outlined blue; Deactivate -> outlined red; Reactivate -> outlined green; Apply, Move,
   Add user -> solid blue; Cancel, Copy, Done -> grey. Hover + keyboard focus in the stylesheet; a disabled one fades (0.5). */
const field = { padding: '6px 8px', borderRadius: 4, border: '1px solid #D0D5DD', fontSize: 13 };

const errMsg = err => err?.response?.data?.error || 'Something went wrong. Please try again.';
const fmtWhen = iso => {
    if (!iso) return 'Never';
    const d = new Date(iso);
    return isNaN(d.getTime()) ? String(iso) : d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function RoleSelect({ value, onChange, ariaLabel }) {
    return (
        <select aria-label={ariaLabel} value={value} onChange={e => onChange(e.target.value)} style={{ ...field, maxWidth: 520 }}>
            <option value="">Choose a role...</option>
            {ASSIGNABLE_ROLES.map(r => <option key={r} value={r}>{roleOptionText(r)}</option>)}
        </select>
    );
}

export default function UserManagementPanel({ companyId, companyName, user, onSelfChanged }) {
    const superAdmin = isSuperAdmin(user);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [error, setError] = useState('');
    const [tempPw, setTempPw] = useState(null);           // { email, password } -- state ONLY, shown once
    const [copied, setCopied] = useState(false);
    const [addEmail, setAddEmail] = useState('');
    const [addRole, setAddRole] = useState('');
    const [busy, setBusy] = useState(false);
    const [roleFor, setRoleFor] = useState(null);          // user id whose role picker is open
    const [roleValue, setRoleValue] = useState('');
    const [moveFor, setMoveFor] = useState(null);          // user id whose move picker is open (super admin)
    const [moveCompany, setMoveCompany] = useState('');
    const [moveRole, setMoveRole] = useState('');
    const [companies, setCompanies] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const res = await listUsersManage(companyId);
            const list = Array.isArray(res.data) ? res.data : [];
            // the server's order, active first (a stable partition, in case a row's status just changed)
            setRows([...list.filter(r => r.is_active), ...list.filter(r => !r.is_active)]);
        } catch (err) {
            setLoadError(errMsg(err));
        }
        setLoading(false);
    }, [companyId]);

    useEffect(() => {
        setTempPw(null); setError(''); setRoleFor(null); setMoveFor(null);
        load();
    }, [load]);

    useEffect(() => {
        if (!superAdmin) return;
        getCompanies().then(res => setCompanies(Array.isArray(res.data) ? res.data : [])).catch(() => setCompanies([]));
    }, [superAdmin]);

    const showTemp = (email, password) => { setCopied(false); setTempPw({ email, password }); };

    const run = async fn => {
        if (busy) return;
        setBusy(true);
        setError('');
        try { await fn(); } catch (err) { setError(errMsg(err)); }
        setBusy(false);
    };

    const add = e => {
        e.preventDefault();
        run(async () => {
            const res = await createUser(companyId, { email: addEmail.trim(), role: addRole });
            showTemp(res.data.user && res.data.user.email, res.data.temporary_password);
            setAddEmail(''); setAddRole('');
            await load();
        });
    };

    const applyRole = row => {
        if (!roleValue || roleValue === row.role) { setRoleFor(null); return; }
        const self = user && row.id === user.id;
        if (self && row.role === 'admin' && roleValue !== 'admin'
            && !window.confirm('Are you sure? This changes YOUR OWN role (' + row.email + ') from ' + roleLabel(row.role) + ' to ' + roleLabel(roleValue)
                + '. You will no longer be able to edit the model or manage users.')) return;
        run(async () => {
            await updateUser(companyId, row.id, { role: roleValue });
            setRoleFor(null);
            if (self) {
                // YOUR OWN role changed: the shell re-reads who you are (GET /api/auth/me). A demoted admin is a viewer now,
                // so this tab closes (AdminPage stops offering it) and the list is not re-read (a viewer is refused it).
                const me = await getMe();
                if (onSelfChanged) onSelfChanged(me.data);
                return;
            }
            await load();
        });
    };

    const deactivate = row => {
        if (!window.confirm('Are you sure you want to deactivate ' + row.email + '? They will be signed out everywhere and cannot sign in until reactivated.')) return;
        run(async () => { await updateUser(companyId, row.id, { is_active: false }); await load(); });
    };
    const reactivate = row => run(async () => { await updateUser(companyId, row.id, { is_active: true }); await load(); });

    const reset = row => {
        if (!window.confirm('Are you sure you want to reset the password for ' + row.email + '? They will be signed out everywhere and must sign in with a new temporary password, then set their own.')) return;
        run(async () => {
            const res = await resetUserPassword(companyId, row.id);
            showTemp(row.email, res.data.temporary_password);
            await load();
        });
    };

    const applyMove = row => {
        const to = companies.find(c => String(c.id) === String(moveCompany));
        if (!to || !moveRole) return;
        if (!window.confirm('Are you sure you want to move ' + row.email + ' from ' + (companyName || 'this company') + ' to ' + to.name + ' as ' + roleLabel(moveRole)
            + '? They will be signed out everywhere; their scenarios stay with ' + (companyName || 'this company') + '.')) return;
        run(async () => {
            await moveUser(row.id, { company_id: to.id, role: moveRole });
            setMoveFor(null);
            setRows(rs => rs.filter(r => r.id !== row.id));
        });
    };

    const copy = async () => {
        if (!tempPw) return;
        try { await navigator.clipboard.writeText(tempPw.password); setCopied(true); } catch (e) { setCopied(false); }
    };

    const otherCompanies = companies.filter(c => String(c.id) !== String(companyId));

    return (
        <div style={{ fontFamily: FONT, maxWidth: 1100 }} data-testid="user-management-panel">
            <h3 style={{ fontSize: 18, margin: '0 0 6px', color: '#1A1C2E' }}>Users{companyName ? ' — ' + companyName : ''}</h3>
            <p style={{ fontSize: 13, color: '#666', margin: '0 0 16px' }}>
                The logins of this company. A new login gets a temporary password, shown once here, and must set its own at first sign-in.
            </p>

            {tempPw && (
                <div data-testid="temp-password-box" style={{ background: '#FFF8E1', border: '1px solid #F5C26B', borderRadius: 6, padding: 16, marginBottom: 16 }}>
                    <div style={{ fontSize: 13, color: '#1A1C2E', marginBottom: 8 }}>Temporary password for <strong>{tempPw.email}</strong>:</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                        <code data-testid="temp-password" style={{ fontFamily: "'Space Mono',monospace", fontSize: 16, background: '#fff', padding: '6px 10px', borderRadius: 4, border: '1px solid #E0C080', userSelect: 'all' }}>{tempPw.password}</code>
                        <button type="button" className="fl-btn fl-btn--grey" onClick={copy}>{copied ? 'Copied' : 'Copy'}</button>
                    </div>
                    <div style={{ fontSize: 13, color: '#8A5A00', marginBottom: 10 }}>{"This password won't be shown again — give it to the user directly."}</div>
                    <button type="button" className="fl-btn fl-btn--grey" onClick={() => { setTempPw(null); setCopied(false); }}>Done</button>
                </div>
            )}

            {error && <div role="alert" style={{ background: '#FEF3F2', color: '#B42318', border: '1px solid #FDA29B', borderRadius: 4, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            <form onSubmit={add} style={{ border: '1px solid #E0E0E0', borderRadius: 6, padding: 16, marginBottom: 20 }} data-testid="add-user-form">
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1C2E', marginBottom: 10 }}>Add user</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <input aria-label="Email" type="email" placeholder="Email" value={addEmail} onChange={e => setAddEmail(e.target.value)} style={{ ...field, width: 260 }} />
                    <RoleSelect ariaLabel="Role for the new user" value={addRole} onChange={setAddRole} />
                    <button type="submit" className="fl-btn fl-btn--primary fl-btn--lg" disabled={!addEmail.trim() || !addRole || busy}>Add user</button>
                </div>
                {addRole && <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>{roleLabel(addRole)}: {ROLE_DESCRIPTIONS[addRole]}</div>}
            </form>

            {loading ? <div style={{ color: '#888', fontSize: 13 }}>Loading users...</div>
                : loadError ? <div role="alert" style={{ color: '#B42318', fontSize: 13 }}>{loadError}</div>
                : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }} data-testid="users-table">
                    <thead>
                        <tr>
                            <th style={th}>Email</th>
                            <th style={th} title={ROLE_TOOLTIP}>Role <span aria-label="About roles" title={ROLE_TOOLTIP} style={{ cursor: 'help' }}>{'ⓘ'}</span></th>
                            <th style={th}>Status</th>
                            <th style={th}>Last sign-in</th>
                            <th style={th}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && <tr><td style={{ ...td, color: '#888' }} colSpan={5}>No logins yet.</td></tr>}
                        {rows.map(row => {
                            const self = !!user && row.id === user.id;
                            const off = !row.is_active;
                            return (
                                <tr key={row.id} data-testid={'user-row-' + row.id} data-deactivated={off ? 'true' : 'false'}
                                    style={{ borderTop: '1px solid #F0F0F0', background: off ? '#FAFAFA' : undefined, color: off ? '#999' : undefined, opacity: off ? 0.7 : 1 }}>
                                    <td style={{ ...td, color: off ? '#999' : td.color }}>{row.email}{self ? ' (you)' : ''}</td>
                                    <td style={{ ...td, color: off ? '#999' : td.color }}>
                                        {roleLabel(row.role)}
                                        {roleFor === row.id && (
                                            <div style={{ marginTop: 6 }}>
                                                <RoleSelect ariaLabel={'New role for ' + row.email} value={roleValue} onChange={setRoleValue} />
                                                <div className="fl-btn-row" style={{ marginTop: 6 }}>
                                                    <button type="button" className="fl-btn fl-btn--primary" disabled={busy} onClick={() => applyRole(row)}>Apply</button>
                                                    <button type="button" className="fl-btn fl-btn--grey" onClick={() => setRoleFor(null)}>Cancel</button>
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                    <td style={{ ...td, color: off ? '#999' : td.color }}>{userStatus(row)}</td>
                                    <td style={{ ...td, color: off ? '#999' : td.color }}>{fmtWhen(row.last_login_at)}</td>
                                    <td style={td}>
                                        <div className="fl-btn-row">
                                        <button type="button" className="fl-btn fl-btn--blue" disabled={busy} onClick={() => { setMoveFor(null); setRoleFor(row.id); setRoleValue(row.role); }}>Change role</button>
                                        {off ? (
                                            <button type="button" className="fl-btn fl-btn--green" disabled={busy} onClick={() => reactivate(row)}>Reactivate</button>
                                        ) : (
                                            <span title={self ? "You can't deactivate your own login." : undefined}>
                                                <button type="button" className="fl-btn fl-btn--red" disabled={busy || self} onClick={() => deactivate(row)}>Deactivate</button>
                                            </span>
                                        )}
                                        <span title={self ? 'To change your own password, use Change password.' : undefined}>
                                            <button type="button" className="fl-btn fl-btn--blue" disabled={busy || self} onClick={() => reset(row)}>Reset password</button>
                                        </span>
                                        {superAdmin && (
                                            <button type="button" className="fl-btn fl-btn--blue" disabled={busy} onClick={() => { setRoleFor(null); setMoveFor(row.id); setMoveCompany(''); setMoveRole(row.role); }}>Move to another company...</button>
                                        )}
                                        </div>
                                        {superAdmin && moveFor === row.id && (
                                            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start' }} data-testid={'move-picker-' + row.id}>
                                                <select aria-label={'Company to move ' + row.email + ' to'} value={moveCompany} onChange={e => setMoveCompany(e.target.value)} style={field}>
                                                    <option value="">Choose a company...</option>
                                                    {otherCompanies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                </select>
                                                <RoleSelect ariaLabel={'Role in the new company for ' + row.email} value={moveRole} onChange={setMoveRole} />
                                                <div className="fl-btn-row">
                                                    <button type="button" className="fl-btn fl-btn--primary" disabled={busy || !moveCompany || !moveRole} onClick={() => applyMove(row)}>Move</button>
                                                    <button type="button" className="fl-btn fl-btn--grey" onClick={() => setMoveFor(null)}>Cancel</button>
                                                </div>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
}
